import { useEffect, useState } from "react";
import { api, apiError, eur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { useI18n } from "@/i18n";
import { PageHeader, EmptyState, Th, Td } from "@/components/common";
import { ListFilter, useListFilter, csvMoney } from "@/components/common/ListFilter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Boxes, Plus, Trash2, PackagePlus, AlertTriangle, Pencil } from "lucide-react";

const emptyItem = { name: "", sku: "", unit: "Stück", cost_price: "", reorder_point: 0, supplier_id: "", length_cm: "", width_cm: "", height_cm: "" };

export default function Packaging() {
  const { user } = useAuth();
  const { t } = useI18n();
  const canEdit = can(user, "packaging:edit");
  const [rows, setRows] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(emptyItem);
  const [ord, setOrd] = useState(null); // {reference, status, lines:[]}
  const [receipts, setReceipts] = useState([]);

  const load = () => {
    api.get("/packaging").then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/packaging/receipts").then((r) => setReceipts(r.data)).catch(() => {});
    api.get("/suppliers").then((r) => setSuppliers([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "de")))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const receive = async (rid) => { try { await api.post(`/packaging/receipts/${rid}/receive`); toast.success(t("st.postedToStock")); load(); } catch (e) { toast.error(apiError(e)); } };

  const openEdit = (r) => { setEditId(r.id); setF({ name: r.name || "", sku: r.sku || "", unit: r.unit || "Stück", cost_price: r.cost_price ?? "", reorder_point: r.reorder_point ?? 0, supplier_id: r.supplier_id || "", length_cm: r.length_cm ?? "", width_cm: r.width_cm ?? "", height_cm: r.height_cm ?? "" }); setOpen(true); };
  const save = async () => {
    const payload = { ...f, cost_price: +f.cost_price || 0, reorder_point: +f.reorder_point || 0, supplier_id: f.supplier_id || null,
        length_cm: f.length_cm === "" ? null : +f.length_cm, width_cm: f.width_cm === "" ? null : +f.width_cm, height_cm: f.height_cm === "" ? null : +f.height_cm };
    try {
      if (editId) { await api.put(`/packaging/${editId}`, payload); toast.success(t("pkg.updated")); }
      else { await api.post("/packaging", payload); toast.success(t("pkg.created")); }
      setOpen(false); setF(emptyItem); setEditId(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (r) => { if (!window.confirm(t("pkg.delConfirm", { name: r.name }))) return; try { await api.delete(`/packaging/${r.id}`); toast.success(t("common.deleted")); load(); } catch (e) { toast.error(apiError(e)); } };
  const delReceipt = async (r) => {
    const msg = `${t("pkg.delReceiptConfirm", { ref: r.reference || r.id.slice(0, 8) })}${r.status === "eingegangen" ? ` ${t("pkg.delReceiptStockNote")}` : ""}`;
    if (!window.confirm(msg)) return;
    try { await api.delete(`/packaging/receipts/${r.id}`); toast.success(t("common.deleted")); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const openOrder = () => setOrd({ reference: "", supplier_id: "", status: "bestellt", lines: [{ packaging_id: rows?.[0]?.id || "", quantity: 1, unit_cost: "" }] });
  const addLine = () => setOrd({ ...ord, lines: [...ord.lines, { packaging_id: rows?.[0]?.id || "", quantity: 1, unit_cost: "" }] });
  const updLine = (i, k, v) => { const l = [...ord.lines]; l[i] = { ...l[i], [k]: v }; setOrd({ ...ord, lines: l }); };
  const saveOrder = async () => {
    try { await api.post("/packaging/receipts", { supplier_id: ord.supplier_id || null, reference: ord.reference, status: ord.status,
        lines: ord.lines.map((l) => ({ packaging_id: l.packaging_id, quantity: +l.quantity || 0, unit_cost: +l.unit_cost || 0 })) });
      toast.success(ord.status === "eingegangen" ? t("st.postedToStock") : t("pkg.orderCreated")); setOrd(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const matFilter = useListFilter(rows, { fields: ["name", "sku", "unit"], storageKey: "packagingItems" });
  const recFilter = useListFilter(receipts, { fields: ["reference", "status"], statusField: "status", storageKey: "packagingReceipts" });

  return (
    <div>
      <PageHeader title={t("pkg.title")} subtitle={t("pkg.subtitle")}
        actions={canEdit && <div className="flex gap-2">
          <Button variant="outline" onClick={openOrder} data-testid="pack-order-btn" disabled={!rows?.length}><PackagePlus className="h-4 w-4 mr-1.5" /> {t("pkg.orderReceive")}</Button>
          <Button onClick={() => { setEditId(null); setF(emptyItem); setOpen(true); }} data-testid="new-pack-btn"><Plus className="h-4 w-4 mr-1.5" /> {t("pkg.new")}</Button>
        </div>} />

      <ListFilter filter={matFilter} testId="packaging" placeholder={t("filter.searchProductSku")}
        exportName="verpackungsmaterial" exportColumns={[
          { label: t("common.name"), value: (r) => r.name },
          { label: "SKU", value: (r) => r.sku || "" },
          { label: t("pkg.dims"), value: (r) => [r.length_cm, r.width_cm, r.height_cm].filter((x) => x != null && x !== "").join("x") },
          { label: t("common.unit"), value: (r) => r.unit || "" },
          { label: t("common.costPrice"), value: (r) => csvMoney(r.cost_price) },
          { label: t("common.stock"), value: (r) => r.stock ?? 0 },
          { label: t("pkg.reorderPoint"), value: (r) => r.reorder_point ?? 0 },
        ]} />

      {rows === null ? <div className="text-muted-foreground">{t("common.loading")}</div> :
        matFilter.filtered.length === 0 ? <EmptyState testId="pack-empty" icon={Boxes} title={t("pkg.emptyTitle")} hint={t("pkg.emptyHint")} /> : (
          <div className="overflow-x-auto rounded-md border border-border bg-card shadow-sm">
            <table className="w-full"><thead className="bg-muted/50"><tr><Th>{t("common.name")}</Th><Th>SKU</Th><Th>{t("pkg.dims")}</Th><Th>{t("common.unit")}</Th><Th right>{t("common.costPrice")}</Th><Th right>{t("common.stock")}</Th><Th right>{t("pkg.reorderPoint")}</Th><Th></Th></tr></thead>
              <tbody data-testid="packaging-list">{matFilter.filtered.map((r) => (
                <tr key={r.id} className={r.under_stock ? "bg-chart-3/5" : ""}>
                  <Td>{r.name}{r.under_stock && <AlertTriangle className="inline h-3.5 w-3.5 ml-1.5 text-chart-3" title={t("pkg.underStock")} />}</Td>
                  <Td mono>{r.sku || "—"}</Td>
                  <Td mono className="text-xs">{(r.length_cm || r.width_cm || r.height_cm) ? `${r.length_cm ?? "–"}×${r.width_cm ?? "–"}×${r.height_cm ?? "–"}` : "—"}</Td>
                  <Td>{t(`unit.${String(r.unit || "").toLowerCase()}`, r.unit)}</Td><Td right mono>{eur(r.cost_price)}</Td>
                  <Td right mono>{r.stock ?? 0}</Td><Td right mono>{r.reorder_point ?? 0}</Td>
                  <Td right>{canEdit && <div className="flex items-center justify-end gap-0.5"><Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-primary" title={t("common.edit")} onClick={() => openEdit(r)} data-testid={`edit-pack-${r.id.slice(0, 8)}`}><Pencil className="h-4 w-4" /></Button><Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive" title={t("common.delete")} onClick={() => del(r)} data-testid={`del-pack-${r.id.slice(0, 8)}`}><Trash2 className="h-4 w-4" /></Button></div>}</Td>
                </tr>))}
              </tbody></table>
          </div>
        )}

      {receipts.length > 0 && (
        <div className="mt-6">
          <h3 className="font-heading text-lg font-semibold mb-2">{t("pkg.receiptsTitle")}</h3>
          <ListFilter filter={recFilter} testId="pack-receipts" withStatus placeholder={t("filter.searchReference")}
            statusLabel={(s) => (s === "eingegangen" ? t("st.received") : t("st.ordered"))}
            exportName="verpackung_eingaenge" exportColumns={[
              { label: t("common.reference"), value: (r) => r.reference || "" },
              { label: t("common.status"), value: (r) => (r.status === "eingegangen" ? t("st.received") : t("st.ordered")) },
              { label: t("common.positions"), value: (r) => (r.lines || []).length },
            ]} />
          <div className="overflow-x-auto rounded-md border border-border bg-card shadow-sm">
            <table className="w-full"><thead className="bg-muted/50"><tr><Th>{t("common.reference")}</Th><Th>{t("common.status")}</Th><Th right>{t("common.positions")}</Th><Th></Th></tr></thead>
              <tbody data-testid="packaging-receipts">{recFilter.filtered.map((r) => (
                <tr key={r.id}>
                  <Td>{r.reference || "—"}</Td>
                  <Td>{r.status === "eingegangen"
                    ? <span className="text-xs text-primary font-medium">{t("st.received")}</span>
                    : <span className="text-xs text-chart-3 font-medium">{t("st.ordered")}</span>}</Td>
                  <Td right mono>{(r.lines || []).length}</Td>
                  <Td right><div className="flex items-center justify-end gap-1">{canEdit && r.status !== "eingegangen" && <Button size="sm" onClick={() => receive(r.id)} data-testid={`pack-receive-${r.id.slice(0, 8)}`}>{t("st.postToStock")}</Button>}{canEdit && <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive" title={t("common.delete")} onClick={() => delReceipt(r)} data-testid={`del-pack-receipt-${r.id.slice(0, 8)}`}><Trash2 className="h-4 w-4" /></Button>}</div></Td>
                </tr>))}
              </tbody></table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? t("pkg.editTitle") : t("pkg.newTitle")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>{t("common.name")} *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" data-testid="pack-name" placeholder={t("pkg.namePh")} /></div>
            <div><Label>SKU</Label><Input value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })} className="mt-1.5" data-testid="pack-sku" /></div>
            <div><Label>{t("common.unit")}</Label><Input value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} className="mt-1.5" /></div>
            <div><Label>{t("pkg.costPriceLabel")}</Label><Input type="number" step="0.01" value={f.cost_price} onChange={(e) => setF({ ...f, cost_price: e.target.value })} className="mt-1.5" data-testid="pack-cost" /></div>
            <div><Label>{t("pkg.reorderPoint")}</Label><Input type="number" value={f.reorder_point} onChange={(e) => setF({ ...f, reorder_point: e.target.value })} className="mt-1.5" data-testid="pack-reorder" /></div>
            <div className="col-span-2">
              <Label>{t("pkg.dimsLong")}</Label>
              <div className="mt-1.5 grid grid-cols-3 gap-2">
                <Input type="number" step="0.1" placeholder={t("pkg.length")} value={f.length_cm} onChange={(e) => setF({ ...f, length_cm: e.target.value })} data-testid="pack-length" />
                <Input type="number" step="0.1" placeholder={t("pkg.width")} value={f.width_cm} onChange={(e) => setF({ ...f, width_cm: e.target.value })} data-testid="pack-width" />
                <Input type="number" step="0.1" placeholder={t("pkg.height")} value={f.height_cm} onChange={(e) => setF({ ...f, height_cm: e.target.value })} data-testid="pack-height" />
              </div>
            </div>
            <div className="col-span-2"><Label>{t("common.supplier")}</Label>
              <Select value={f.supplier_id || "none"} onValueChange={(v) => setF({ ...f, supplier_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder={t("common.supplier")} /></SelectTrigger>
                <SelectContent><SelectItem value="none">— {t("common.noSupplier")} —</SelectItem>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={save} disabled={!f.name} data-testid="save-pack-btn">{t("common.save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!ord} onOpenChange={(o) => !o && setOrd(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{t("pkg.orderDialog")}</DialogTitle></DialogHeader>
          {ord && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("common.reference")}</Label><Input value={ord.reference} onChange={(e) => setOrd({ ...ord, reference: e.target.value })} className="mt-1.5" data-testid="pack-ord-ref" /></div>
                <div><Label>{t("common.status")}</Label>
                  <Select value={ord.status} onValueChange={(v) => setOrd({ ...ord, status: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="pack-ord-status"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="bestellt">{t("pkg.statusOrdered")}</SelectItem><SelectItem value="eingegangen">{t("pkg.statusReceived")}</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              {ord.lines.map((l, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Select value={l.packaging_id} onValueChange={(v) => updLine(i, "packaging_id", v)}>
                    <SelectTrigger className="flex-1" data-testid={`pack-ord-item-${i}`}><SelectValue placeholder={t("pkg.material")} /></SelectTrigger>
                    <SelectContent>{(rows || []).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" className="w-20" placeholder={t("common.quantity")} value={l.quantity} onChange={(e) => updLine(i, "quantity", e.target.value)} data-testid={`pack-ord-qty-${i}`} />
                  <Input type="number" step="0.01" className="w-24" placeholder={t("pkg.unitCostShort")} value={l.unit_cost} onChange={(e) => updLine(i, "unit_cost", e.target.value)} />
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={addLine}><Plus className="h-3.5 w-3.5 mr-1" /> {t("common.position")}</Button>
            </div>
          )}
          <DialogFooter><Button onClick={saveOrder} data-testid="save-pack-order-btn">{t("common.save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
